/** @jsx jsx */

import { ClassAttributes, useEffect, } from 'react';

import { jsx, css, } from '@emotion/core';
import styled from '@emotion/styled';
import { typography, } from '@argo/principles';
import { Expander, } from '@argo/base-patterns';

import { ExpertReviewAndRating, } from '../../sew-core/types';

const Container = styled.div`
  width: 100%;
`;

const ParagraphContainer = styled.div`
  padding: 8px 0;
  h4 {
    font-family: Open Sans Regular, sans-serif;
  }
`;

const SectionTitle = styled.div`
  ${typography.types.XSSemi};
  padding: 12px 0 0 0;
`;

interface ExpertReviewProps extends ClassAttributes<any> {
  expertReviewAndRating: ExpertReviewAndRating;
  onExpertReviewsReadMoreClick: () => void;
}

export default ({ expertReviewAndRating, onExpertReviewsReadMoreClick, }: ExpertReviewProps,): JSX.Element => {
  const expertReviewProsId = 'expertReviewPros';
  const expertReviewConsId = 'expertReviewCons';
  const expertReviewEditorOverviewId = 'expertReviewEditorOverview';

  const consIsList = (expertReviewAndRating.cons.text.indexOf('<li>',) > -1);

  const prosAndConsStyle = css`
    * {
      ${typography.types.body}
    }
    ul {
      ${consIsList ? 'padding-left: 16px;' : ''}
      li {
        padding-top: 5px;
      }
    }
  `;

  const overviewIsList = (expertReviewAndRating.editorOverview.text.indexOf('<li>',) > -1);

  const overviewStyle = css`
    * {
      ${typography.types.body}
    }
    ul {
      ${overviewIsList ? 'padding-left: 16px;' : ''}
    }
  `;

  const assignClickEventHandler = (expanderId: string,) => {
    let expanderText = document.querySelector(`#${expanderId}-expander`,)?.querySelector('button',)?.innerText;
    expanderText = expanderText?.toLowerCase();
    if (expanderText === 'read more') {
      onExpertReviewsReadMoreClick();
    }
  };

  /**
   Processes any hyperlinks from the editor overview text to pop up a new window or tab when clicked
   **/
  const processLinkInOverviewText = (processedText) => {
    let regex = new RegExp('<a ', 'g');
    processedText = processedText.replace(regex, '<a target="_blank" ');

    return processedText;
  };

  useEffect(() => {
    const overviewExpander = document.querySelector(`#${expertReviewEditorOverviewId}-expander`,)?.querySelector('button',);

    if (overviewExpander) { // conditionally add click event listener on first render
      overviewExpander.addEventListener('click', () => {
        assignClickEventHandler(expertReviewEditorOverviewId,);
      },);
    }

    return () => { // remove event listener on clean up
      if (overviewExpander) {
        overviewExpander.addEventListener('click', () => { assignClickEventHandler(expertReviewEditorOverviewId,); },);
      }
    };
  },);

  if(expertReviewAndRating.editorOverview.text.length === 0){
    return(
      <Container>
        There are no reviews for this vehicle.
      </Container>
    )
  }
  
  expertReviewAndRating.editorOverview.text = processLinkInOverviewText(expertReviewAndRating.editorOverview.text);

  return (
    <Container>
      <ParagraphContainer id={expertReviewProsId}>
        <SectionTitle>{expertReviewAndRating.pros.title}</SectionTitle>
        <span css={prosAndConsStyle} dangerouslySetInnerHTML={{ __html: expertReviewAndRating.pros.text, }} />
      </ParagraphContainer>
      <ParagraphContainer id={expertReviewConsId}>
        <SectionTitle>{expertReviewAndRating.cons.title}</SectionTitle>
        <span css={prosAndConsStyle} dangerouslySetInnerHTML={{ __html: expertReviewAndRating.cons.text, }} />
      </ParagraphContainer>
      <ParagraphContainer style={{ paddingBottom: '16px', }} id={expertReviewEditorOverviewId}>
        <SectionTitle>{expertReviewAndRating.editorOverview.title}</SectionTitle>
        <div id={`${expertReviewEditorOverviewId}-expander`}>
          <Expander maxLines={4}>
            <span css={overviewStyle} dangerouslySetInnerHTML={{ __html: expertReviewAndRating.editorOverview.text, }} />
          </Expander>
        </div>
      </ParagraphContainer>
    </Container>
  );
};
